import React, { createContext, useState, useContext, useCallback, useEffect } from 'react';
import api from '../services/api';

const AdminAuthContext = createContext();

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
};

export const AdminAuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('admin_token'));

  const logout = useCallback(() => {
    localStorage.removeItem('admin_token');
    setToken(null);
    setAdmin(null);
  }, []);

  const verifyAdminToken = useCallback(async () => {
    try {
      const response = await api.verifyAdminToken(token);
      if (response.data.valid) {
        setAdmin(response.data.admin);
      } else {
        logout();
      }
    } catch (error) {
      console.error('Admin token verification failed:', error);
      logout();
    } finally {
      setLoading(false);
    }
  }, [token, logout]);

  const login = async (email, password) => {
    try {
      const response = await api.adminLogin({ email, password });
      const { token: newToken, admin: adminData } = response.data;
      
      localStorage.setItem('admin_token', newToken);
      setToken(newToken);
      setAdmin(adminData);
      
      return { success: true };
    } catch (error) {
      console.error('Admin login failed:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || 'Login failed' 
      };
    }
  };

  const isAuthenticated = useCallback(() => {
    return !!admin && !!token;
  }, [admin, token]);

  const isSuperAdmin = useCallback(() => {
    return admin?.role === 'super_admin';
  }, [admin]);

  useEffect(() => {
    if (token) {
      verifyAdminToken();
    } else {
      setLoading(false);
    }
  }, [token, verifyAdminToken]);

  const value = {
    admin,
    loading,
    login,
    logout,
    isAuthenticated,
    isSuperAdmin,
    verifyAdminToken
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};